import React, { useState, useMemo } from 'react';
import { AgGridReact } from 'ag-grid-react';
import type { ColDef } from 'ag-grid-community';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-alpine.css';
import { Chip, Accordion, AccordionItem, Input } from '@nextui-org/react';
import { Icon } from '@iconify/react';
import { ErrorDisplay } from './ErrorDisplay';
import { parseScore, formatCurrency } from '../utils/dateutils';
import { processErrorBody, extractRendererData } from './rendererUtils';
import { RendererHeader } from './RendererHeader';
import { ScoreCard } from './ScoreCard';
import { useScoreColor } from './claimScoreRendererShared';
import { RendererTabs } from './RendererTabs';
import { FullScreenModal } from './FullScreenModal';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import 'github-markdown-css/github-markdown.css';

export interface InvoiceMatcherRendererProps {
    claimData: any;
    onButtonAction?: (action: string, buttonData: any) => void;
    showActionButtons?: boolean;
    className?: string;
    isModal?: boolean;
    source?: 'agent_history' | 'actions';
    feedbackComponent?: React.ReactNode;
    knowledgeComponent?: React.ReactNode;
    onRefresh?: () => void;
    /** When provided, "Full View" delegates to this instead of the built-in modal (e.g. widget host opening a real fullscreen tab). */
    onRequestFullScreen?: () => void;
}

const InvoiceMatcherRenderer: React.FC<InvoiceMatcherRendererProps> = ({
    claimData,
    onButtonAction,
    showActionButtons = true,
    className = '',
    isModal = false,
    source = 'agent_history',
    feedbackComponent,
    knowledgeComponent,
    onRefresh,
    onRequestFullScreen
}) => {
    const [selectedKeys, setSelectedKeys] = useState<Set<string>>(new Set());
    const [selectedDetailKeys, setSelectedDetailKeys] = useState<Set<string>>(new Set());
    const [isFullScreenOpen, setIsFullScreenOpen] = useState(false);
    const [searchQuery, setSearchQuery] = useState<string>('');
    const [activeFilter, setActiveFilter] = useState<'matches' | 'partial' | 'mismatches' | null>(null);
    const [activeStopGate, setActiveStopGate] = useState<string | null>(null);
    const [isExpanded, setIsExpanded] = useState(false);
    const [activeTab, setActiveTab] = useState<string>('overview');

    // Extract data safely - process claimData before any early returns
    const { data, agentScore, duration, entityNumber, timestamp, aiSummary, agentName, actionName } = useMemo(
        () => extractRendererData(claimData),
        [claimData]
    );

    const shouldRender = useMemo(() => {
        if (!data) return false;
        return source === 'agent_history'
            ? (agentName.toLowerCase().includes('invoice matcher') || 
               agentName.toLowerCase().includes('invoice_matcher') ||
               agentName.toLowerCase().includes('ro matcher') ||
               agentName.toLowerCase().includes('ro_matcher'))||
               agentName.toLowerCase().includes('maintenance claim')||
               agentName.toLowerCase().includes('maintenance_claim')
            : (actionName.toLowerCase() === 'invoice matcher' || 
               actionName.toLowerCase() === 'ro matcher' ||
               actionName.toLowerCase() === 'maintenance claim');
    }, [data, source, agentName, actionName]);


    // Process invoice matcher data
    const invoiceMatcherDataResult = useMemo(() => {
        const mergeSubletFlags = (...sources: any[]) => {
            const merged: { sublet_found?: boolean; sublet_match_found?: boolean } = {};
            for (const src of sources) {
                if (!src || typeof src !== 'object') continue;
                if (typeof src.sublet_found === 'boolean') merged.sublet_found = src.sublet_found;
                if (typeof src.sublet_match_found === 'boolean') merged.sublet_match_found = src.sublet_match_found;
            }
            return merged;
        };
        const emptySublet: { sublet_found?: boolean; sublet_match_found?: boolean } = {};
        const emptyStopGates: string[] = [];
        const extractStopGates = (source: any): string[] => {
            const gates = source?.stop_gates;
            if (!Array.isArray(gates)) return emptyStopGates;
            return gates.filter((g: any): g is string => typeof g === 'string' && g.trim().length > 0);
        };

        if (!data) {
            return { explanationData: [], totalScore: '', errorData: null, isRepairOrderMatching: false, subletFlags: emptySublet, stopGates: emptyStopGates };
        }

        const invoiceMatcherData = data?.action_output?.["Invoice Matcher"] || 
                                   data?.action_output?.["RO Matcher"] ||
                                   data?.action_output?.["Maintenance Claim"];
        let explanationData: any[] = [];
        let totalScore: any = '';
        let errorData: any = null;
        let isRepairOrderMatching = false;

        if (!invoiceMatcherData) {
            return { explanationData, totalScore, errorData, isRepairOrderMatching, subletFlags: emptySublet, stopGates: emptyStopGates };
        }

        if (Array.isArray(invoiceMatcherData)) {
            // Check if any item in the array has repair_order_matching structure
            // Structure: invoiceMatcherData[0].explanation.repair_order_matching.explanation
            for (const item of invoiceMatcherData) {
                const repairOrderMatching = item?.explanation?.repair_order_matching;
                if (repairOrderMatching?.explanation && Array.isArray(repairOrderMatching.explanation)) {
                    explanationData = [...repairOrderMatching.explanation];
                    totalScore = String(invoiceMatcherData[0]?.maintenance_claim_score || '');
                    isRepairOrderMatching = true;
                    
                    // Also check for preconditions_check and add it to explanationData at the top
                    const preconditionsCheck = item?.explanation?.preconditions_check;
                    if (preconditionsCheck?.details_evaluation && Array.isArray(preconditionsCheck.details_evaluation)) {
                        explanationData = [{
                            criteria: 'Eligibility Check Details',
                            score: preconditionsCheck.precondition_score || 0,
                            details_evaluation: preconditionsCheck.details_evaluation,
                            isPreconditionsCheck: true
                        }, ...explanationData];
                    }
                    
                    const subletFlags = mergeSubletFlags(item, item?.explanation);
                    return { explanationData, totalScore, errorData, isRepairOrderMatching, subletFlags, stopGates: extractStopGates(item) };
                }
            }
            
            // Fallback to original array handling
            explanationData = invoiceMatcherData.flatMap((item: any) => item.explanation || []);
            totalScore = invoiceMatcherData[0]?.total_score || '';
            const first = invoiceMatcherData[0];
            const subletFlags = mergeSubletFlags(first, first?.explanation);
            return { explanationData, totalScore, errorData, isRepairOrderMatching, subletFlags, stopGates: extractStopGates(first) };
        }

        const hasError = invoiceMatcherData.error || invoiceMatcherData.body;
        if (hasError) {
            errorData = {
                error: invoiceMatcherData.error,
                body: invoiceMatcherData.body
            };
            const parsedBody = processErrorBody(invoiceMatcherData.body);
            if (parsedBody !== null) {
                errorData.parsedBody = parsedBody;
            }
            return { explanationData, totalScore, errorData, isRepairOrderMatching, subletFlags: emptySublet, stopGates: emptyStopGates };
        }

        // Check for repair_order_matching structure (nested in explanation for object format)
        const repairOrderMatching = invoiceMatcherData.explanation?.repair_order_matching || 
                                   invoiceMatcherData.repair_order_matching;
        if (repairOrderMatching?.explanation && Array.isArray(repairOrderMatching.explanation)) {
            explanationData = [...repairOrderMatching.explanation];
            totalScore = String(invoiceMatcherData.maintenance_claim_score || '');
            isRepairOrderMatching = true;
            
            // Also check for preconditions_check and add it to explanationData at the top
            const preconditionsCheck = invoiceMatcherData.explanation?.preconditions_check;
            if (preconditionsCheck?.details_evaluation && Array.isArray(preconditionsCheck.details_evaluation)) {
                explanationData = [{
                    criteria: 'Eligibility Check Details',
                    score: preconditionsCheck.precondition_score || 0,
                    details_evaluation: preconditionsCheck.details_evaluation,
                    isPreconditionsCheck: true
                }, ...explanationData];
            }
            
            const subletFlags = mergeSubletFlags(invoiceMatcherData, invoiceMatcherData.explanation);
            return { explanationData, totalScore, errorData, isRepairOrderMatching, subletFlags, stopGates: extractStopGates(invoiceMatcherData) };
        }

        explanationData = invoiceMatcherData.explanation || [];
        totalScore = invoiceMatcherData.total_score || '';
        const subletFlags = mergeSubletFlags(invoiceMatcherData, invoiceMatcherData.explanation);
        return { explanationData, totalScore, errorData, isRepairOrderMatching, subletFlags, stopGates: extractStopGates(invoiceMatcherData) };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [data]);

    const explanationData = invoiceMatcherDataResult.explanationData;
    const totalScore = invoiceMatcherDataResult.totalScore;
    const errorData = invoiceMatcherDataResult.errorData;
    const isRepairOrderMatching = invoiceMatcherDataResult.isRepairOrderMatching;
    const subletFlags = invoiceMatcherDataResult.subletFlags;
    const stopGates = invoiceMatcherDataResult.stopGates;

    // Agent-config driven score color (falls back to 85/60 defaults when no provider/config).
    const getScoreColor = useScoreColor();

    // Calculate match breakdown from explanation data
    const matchBreakdown = useMemo(() => {
        if (!explanationData || explanationData.length === 0) {
            return { matches: 0, partial: 0, mismatches: 0 };
        }

        const breakdown = { matches: 0, partial: 0, mismatches: 0 };
        explanationData.forEach((item: any) => {
            const matchScore = item.score ?? (item['Match score'] || item['match_score'] || item['Match Score'] || item['Score'] || '');
            if (matchScore === '' || matchScore == null) return;
            const numScore = parseScore(matchScore);

            if (numScore >= 85) breakdown.matches++;
            else if (numScore >= 60) breakdown.partial++;
            else breakdown.mismatches++;
        });

        return breakdown;
    }, [explanationData, isRepairOrderMatching]);

    const formatValue = (value: any, indent: number = 0): string => {
        if (value === null || value === undefined) {
            return 'N/A';
        }

        if (typeof value === 'boolean') {
            return value ? 'True' : 'False';
        }

        if (Array.isArray(value)) {
            return value.map((item: any) => String(item)).join(', ');
        }

        if (typeof value === 'object') {
            const indentLevel = indent === 0 ? 1 : indent;
            const indentStr = '  '.repeat(indentLevel);
            const lines: string[] = [];
            
            Object.entries(value).forEach(([key, val]) => {
                if (Array.isArray(val)) {
                    lines.push(`${indentStr}${key}: ${val.map((item: any) => typeof item === 'number' ? formatCurrency(item) : String(item)).join(', ')}`);
                } else if (typeof val === 'object' && val !== null) {
                    lines.push(`${indentStr}${key}:`);
                    lines.push(formatValue(val, indentLevel + 1));
                } else {
                    lines.push(`${indentStr}${key}: ${typeof val === 'number' ? formatCurrency(val) : String(val)}`);
                }
            });
            
            return lines.join('\n');
        }

        return String(value);
    };

    // Helper function to extract description from matches array
    const extractDescriptionFromMatches = (matches: any[]): string => {
        if (!Array.isArray(matches) || matches.length === 0) {
            return '';
        }
        const descriptions = matches
            .map((match: any) => match?.description)
            .filter((desc: any) => desc !== null && desc !== undefined && desc !== '');
        return descriptions.length > 0 ? descriptions.join('\n\n') : '';
    };

    // Get all unique keys to find the right field names (moved outside to avoid recalculation)
    const criteriaKey = useMemo(() =>
        ['Criteria', 'criteria', 'Field'].find(key =>
            explanationData.some((item: any) => item[key] !== undefined)
        ) || 'Criteria',
        [explanationData]
    );

    const invoiceKey = useMemo(() =>
        ['Invoice', 'invoice', 'Invoice Value'].find(key =>
            explanationData.some((item: any) => item[key] !== undefined)
        ) || 'Invoice',
        [explanationData]
    );

    const claimKey = useMemo(() =>
        ['Claim', 'claim', 'Claim Value'].find(key =>
            explanationData.some((item: any) => item[key] !== undefined)
        ) || 'Claim',
        [explanationData]
    );

    const matchScoreKey = useMemo(() =>
        ['score', 'match_score', 'Match Score', 'Score'].find(key =>
            explanationData.some((item: any) => item[key] !== undefined)
        ) || 'score',
        [explanationData]
    );

    const notesKey = useMemo(() =>
        ['Notes', 'notes', 'Note', 'note'].find(key =>
            explanationData.some((item: any) => item[key] !== undefined)
        ) || 'Notes',
        [explanationData]
    );

    // Helper to get score from item
    const getItemScore = (item: any): any => {
        return isRepairOrderMatching ? (item.score || '') : (item[matchScoreKey] || '');
    };

    // Helper to get searchable text from item
    const getItemSearchText = (item: any): string => {
        if (isRepairOrderMatching) {
            const criteria = formatValue(item.criteria).toLowerCase();
            const invoice = formatValue(item.invoice).toLowerCase();
            const claim = formatValue(item.claim).toLowerCase();
            const matchScore = String(item.score || '').toLowerCase();
            const matching = formatValue(item.matching).toLowerCase();
            
            let lineMatchesText = '';
            if (item.line_matches && Array.isArray(item.line_matches)) {
                lineMatchesText = item.line_matches.map((lm: any) => {
                    const desc = extractDescriptionFromMatches(lm.matches || []);
                    return `${formatValue(lm.detail_note)} ${formatValue(lm.claim_amount)} ${formatValue(lm.score)} ${formatValue(lm.match_found)} ${formatValue(lm.amount_match_found)} ${desc}`;
                }).join(' ').toLowerCase();
            }

            // Include preconditions_check details_evaluation data in search
            let preconditionsText = '';
            if (item.isPreconditionsCheck && item.details_evaluation && Array.isArray(item.details_evaluation)) {
                preconditionsText = item.details_evaluation.map((detail: any) => {
                    return `${formatValue(detail.note)} ${formatValue(detail.detail_note)} ${formatValue(detail.detail_check)} ${formatValue(detail.request_amount)} ${formatValue(detail.service_amount)} ${formatValue(detail.date_in_range)} ${formatValue(detail.amount_in_range)} ${formatValue(detail.total_allowed_this_year)} ${formatValue(detail.claimed_this_year)} ${formatValue(detail.remaining_claimable_this_year)}`;
                }).join(' ').toLowerCase();
            }

            return `${criteria} ${invoice} ${claim} ${matchScore} ${matching} ${lineMatchesText} ${preconditionsText}`;
        } else {
            const criteria = formatValue(item[criteriaKey]).toLowerCase();
            const invoice = formatValue(item[invoiceKey]).toLowerCase();
            const claim = formatValue(item[claimKey]).toLowerCase();
            const matchScore = String(item[matchScoreKey] || '').toLowerCase();
            const notes = formatValue(item[notesKey]).toLowerCase();
            const signed = item.signed == null || item.signed === '' ? '' : formatValue(item.signed).toLowerCase();
            const evidenceReference = item.evidence_reference == null || (Array.isArray(item.evidence_reference) && item.evidence_reference.length === 0)
                ? ''
                : formatValue(item.evidence_reference).toLowerCase();
            return `${criteria} ${invoice} ${claim} ${matchScore} ${notes} ${signed} ${evidenceReference}`;
        }
    };

    // Stop gate items shown in the ScoreCard's "Stop gates" accordion
    const stopGateItems = useMemo(() => {
        if (!stopGates || stopGates.length === 0) return [];
        return stopGates.map((gate: string) => {
            const match = explanationData.find((item: any) => {
                const criteria = isRepairOrderMatching ? formatValue(item.criteria) : formatValue(item[criteriaKey]);
                return criteria.trim() === gate.trim();
            });
            return { key: gate, label: gate, score: match ? getItemScore(match) : undefined };
        });
    }, [stopGates, explanationData, criteriaKey, isRepairOrderMatching]);

    // Filter data based on search query, match filter, and stop gate selection
    const filteredData = useMemo(() => {
        if (!explanationData || explanationData.length === 0) {
            return [];
        }

        let filtered = explanationData;

        // Stop gate filter is mutually exclusive with the match-type filter and search box
        if (activeStopGate !== null) {
            return filtered.filter((item: any) => {
                const criteria = isRepairOrderMatching ? formatValue(item.criteria) : formatValue(item[criteriaKey]);
                return criteria.trim() === activeStopGate.trim();
            });
        }

        // Apply match type filter
        if (activeFilter !== null) {
            filtered = filtered.filter((item: any) => {
                const rawScore = isRepairOrderMatching ? item.score : item[matchScoreKey];
                if (rawScore === undefined || rawScore === null || rawScore === '') return false;
                const numScore = parseScore(rawScore);
                if (activeFilter === 'matches') return numScore >= 85;
                if (activeFilter === 'partial') return numScore >= 60 && numScore < 85;
                if (activeFilter === 'mismatches') return numScore < 60;
                return true;
            });
        }

        // Apply search query filter
        if (searchQuery.trim()) {
            const query = searchQuery.toLowerCase().trim();
            filtered = filtered.filter((item: any) => 
                getItemSearchText(item).includes(query)
            );
        }

        return filtered;
    }, [explanationData, searchQuery, activeFilter, activeStopGate, criteriaKey, invoiceKey, claimKey, matchScoreKey, notesKey, isRepairOrderMatching]);

    // Generate all possible keys for expand all functionality
    const generateAllKeys = () => {
        const mainKeys = new Set<string>(['ai-summary', 'detailed-summary']);
        const detailKeys = new Set<string>();

        // Generate keys for all currently visible detail items (matching renderDetailedSummary logic)
        if (filteredData && filteredData.length > 0) {
            for (let index = 0; index < filteredData.length; index++) {
                const item = filteredData[index];
                if (isRepairOrderMatching) {
                    // For new format, use index directly since we don't need to find original index
                    detailKeys.add(`detail-${index}`);
                } else {
                    const criteria = formatValue(item[criteriaKey]);
                    const originalIndex = explanationData.findIndex((origItem: any) =>
                        formatValue(origItem[criteriaKey]) === criteria
                    );
                    const keyIndex = originalIndex >= 0 ? originalIndex : index;
                    detailKeys.add(`detail-${keyIndex}`);
                }
            }
        }

        return { mainKeys, detailKeys };
    };

    const handleExpandAll = () => {
        if (isExpanded) {
            setSelectedKeys(new Set());
            setSelectedDetailKeys(new Set());
            setIsExpanded(false);
        } else {
            const { mainKeys, detailKeys } = generateAllKeys();
            setSelectedKeys(mainKeys);
            setSelectedDetailKeys(detailKeys);
            setIsExpanded(true);
        }
    };

    const handleFilterClick = (filterType: 'matches' | 'partial' | 'mismatches') => {
        const newFilter = activeFilter === filterType ? null : filterType;
        setActiveFilter(newFilter);
        setActiveStopGate(null);
        // Open detailed summary accordion when filter is applied
        if (newFilter !== null) {
            setSelectedKeys(prev => new Set([...prev, 'detailed-summary']));
        }
    };

    const handleStopGateClick = (gate: string) => {
        if (activeStopGate === gate) {
            setActiveStopGate(null);
            return;
        }
        setActiveFilter(null);
        setSearchQuery('');
        setActiveStopGate(gate);
        setSelectedKeys(prev => new Set([...prev, 'detailed-summary']));

        const matchIndex = explanationData.findIndex((item: any) => {
            const criteria = isRepairOrderMatching ? formatValue(item.criteria) : formatValue(item[criteriaKey]);
            return criteria.trim() === gate.trim();
        });
        if (matchIndex < 0) {
            setSelectedDetailKeys(new Set());
        } else if (isRepairOrderMatching) {
            const match = explanationData[matchIndex];
            const criteria = formatValue(match.criteria);
            const score = match.score ? match.score : (match[matchScoreKey] || 0);
            setSelectedDetailKeys(new Set([`detail-${criteria}-${score}-0`]));
        } else {
            setSelectedDetailKeys(new Set([`detail-${matchIndex}`]));
        }
    };

    // Early returns after all hooks
    if (!data) {
        return (
            <div className={`flex items-center justify-center py-12 ${className}`}>
                <div className="text-default-500">No claim data available</div>
            </div>
        );
    }

    if (!shouldRender) {
        return null;
    }

    // Helper function to render score chip
    const renderScoreChip = (scoreColor: string, scoreDisplay: string) => {
        if (scoreColor === 'warning') {
            return (
                <Chip
                    variant="flat"
                    size="sm"
                    style={{
                        backgroundColor: '#FEF3C7',
                        color: '#F59E0B'
                    }}
                >
                    {scoreDisplay}
                </Chip>
            );
        }
        return (
            <Chip
                color={scoreColor as 'success' | 'danger' | 'default'}
                variant="flat"
                size="sm"
            >
                {scoreDisplay}
            </Chip>
        );
    };

    // Key-value row: if value (as string) length < 100 → side by side, value right-aligned; else value below key
    const renderKeyValue = (keyLabel: string, value: any, context?: { criteria?: string }) => {
        let strVal = formatValue(value);
        const isAmountKey = ((keyLabel.toLowerCase().includes('amount') || keyLabel.toLowerCase().includes('price') || keyLabel.toLowerCase().includes('cost') || keyLabel.toLowerCase().includes('invoice') || keyLabel.toLowerCase().includes('claim')) && (!keyLabel.toLowerCase().includes('percentage') && !keyLabel.toLowerCase().includes('year'))) && typeof value === 'number';
        const criteriaExcludesCurrency = context?.criteria?.toLowerCase().includes('readings');
        if (isAmountKey && !criteriaExcludesCurrency) {
            strVal = formatCurrency(Number(value));
        }
        const useInline = strVal.length < 50;
        const layoutClass = useInline ? 'renderer-key-value-inline' : 'renderer-key-value-stacked';
        return (
            <div className={`renderer-text-base ${layoutClass}`}>
                <span style={{ color: 'var(--cty-text-muted)' }}>{keyLabel}:</span>
                {useInline ? (
                    <span style={{ color: 'var(--cty-text-primary)' }}>{strVal}</span>
                ) : (
                    <div style={{ color: 'var(--cty-text-primary)', whiteSpace: 'pre-line' }}>{strVal}</div>
                )}
            </div>
        );
    };

    // Helper function to render accordion content
    const renderAccordionContent = (item: any, isServiceEvent: boolean) => {
        if (isServiceEvent && item.line_matches) {
            return renderServiceEventTable(item.line_matches);
        }

        // Check for preconditions_check
        if (item.isPreconditionsCheck && item.details_evaluation) {
            return renderPreconditionsCheckTable(item.details_evaluation);
        }

        const invoiceValue = isRepairOrderMatching ? item.invoice : item[invoiceKey];
        const claimValue = isRepairOrderMatching ? item.claim : item[claimKey];
        const notes = isRepairOrderMatching ? null : item[notesKey];
        const matching = isRepairOrderMatching ? item.matching : null;
        const hasValue = (value: any) => {
            if (value == null || value === '') return false;
            if (Array.isArray(value)) return value.length > 0;
            return true;
        };

        const hasInvoice = isRepairOrderMatching ? 'invoice' in item : invoiceKey in item;
        const hasClaim = isRepairOrderMatching ? 'claim' in item : claimKey in item;

        return (
            <>
                {hasInvoice && renderKeyValue('Invoice', invoiceValue, { criteria: isRepairOrderMatching ? item.criteria : item[criteriaKey] })}
                {hasClaim && renderKeyValue('Claim', claimValue, { criteria: isRepairOrderMatching ? item.criteria : item[criteriaKey] })}
                {matching != null && renderKeyValue('Matching', matching)}
                {notes != null && formatValue(notes) !== 'N/A' && renderKeyValue('Notes', notes)}
                {hasValue(item.signed) && renderKeyValue('Signed', item.signed)}
                {hasValue(item.evidence_reference) && renderKeyValue('Evidence Reference', item.evidence_reference)}
            </>
        );
    };

        // Helper function to render Preconditions Check table (nested accordions inside Eligibility Check Details)
        const renderPreconditionsCheckTable = (detailsEvaluation: any[]) => {
            if (!Array.isArray(detailsEvaluation) || detailsEvaluation.length === 0) {
                return <div className="text-default-500">No details evaluation available</div>;
            }

            return (
                <Accordion variant="light" isCompact selectionMode="multiple" className="!px-1 !bg-content1 w-full gap-0 [&_hr]:hidden" dividerProps={{ className: 'hidden' }}>
                    {detailsEvaluation.map((detail: any, idx: number) => {
                        const uniqueKey = `precond-${idx}-${formatValue(detail.detail_note)}`;
                        const title = formatValue(detail.detail_note);
                        return (
                            <AccordionItem key={uniqueKey} aria-label={title} 
                            title={
                                <div className="flex items-center w-full gap-2">
                                    <div className="flex items-center gap-1 whitespace-nowrap">
                                        <span className="text-[#667085] text-[14px]">{title}</span>
                                        <Icon
                                            icon="mdi:chevron-down"
                                            className="text-[#667085] w-4 h-4 transition-transform group-aria-expanded:rotate-180"
                                        />
                                    </div>
                                    <div className="flex-1 border-b border-default-300"></div>
                                    {detail.detail_check === 'Passed' ? renderScoreChip('success', '100%') : renderScoreChip('danger', '0%')}
                                </div>
                            } 
                            indicator={<></>}
                            classNames={{
                                content: "pl-0 pr-0",
                                base: "w-full !border-none !shadow-none !bg-transparent !px-0",
                                title: "text-base w-full",
                                trigger: "group py-2"
                            }}
                            className="[&>h2>button]:block">
                                <div className="flex flex-col gap-1 pb-2">
                                    {renderKeyValue('Detail Note', detail.detail_note)}
                                    {renderKeyValue('Request Amount', detail.request_amount)}
                                    {renderKeyValue('Service Amount', detail.service_amount)}
                                    {renderKeyValue('Date In Range', detail.date_in_range)}
                                    {renderKeyValue('Amount In Range', detail.amount_in_range)}
                                    {renderKeyValue('Note', detail.note)}
                                    {renderKeyValue('Total Allowed This Year', detail.total_allowed_this_year)}
                                    {renderKeyValue('Claimed This Year', detail.claimed_this_year)}
                                    {renderKeyValue('Remaining Claimable This Year', detail.remaining_claimable_this_year)}
                                    {renderKeyValue('Detail Check', detail.detail_check)}
                                </div>
                            </AccordionItem>
                        );
                    })}
                </Accordion>
            );
        };

    // Helper function to render Service Event table
    const renderServiceEventTable = (lineMatches: any[]) => {
        if (!Array.isArray(lineMatches) || lineMatches.length === 0) {
            return <div className="text-default-500">No line matches available</div>;
        }

        const rowData = lineMatches.map((lineMatch: any, idx: number) => ({
            id: `${formatValue(lineMatch.detail_note)}-${formatValue(lineMatch.claim_amount)}-${idx}`,
            detail_note: formatValue(lineMatch.detail_note),
            claim_amount: formatCurrency(Number(lineMatch.claim_amount)),
            description: extractDescriptionFromMatches(lineMatch.matches || []),
            match_found: formatValue(lineMatch.match_found),
            score: formatValue(lineMatch.score),
        }));

        const columnDefs: ColDef[] = [
            {
                field: 'detail_note',
                headerName: 'Detail Note',
                flex: 1,
                minWidth: 150,
                wrapText: true,
                autoHeight: true,
                cellClass: 'font-medium text-foreground break-words !leading-snug !py-2',
                cellStyle: { wordWrap: 'break-word', overflowWrap: 'break-word', whiteSpace: 'normal' },
            },
            {
                field: 'claim_amount',
                headerName: 'Claim Amount',
                flex: 0.8,
                minWidth: 130,
                cellClass: 'text-right break-words',
                headerClass: 'text-right font-semibold text-default-700',
                cellStyle: { wordWrap: 'break-word', overflowWrap: 'break-word' },
            },
            {
                field: 'description',
                headerName: 'Description',
                flex: 1.5,
                minWidth: 250,
                wrapText: true,
                autoHeight: true,
                cellClass: 'break-words !leading-snug !py-2',
                cellStyle: {
                    whiteSpace: 'pre-line',
                    wordWrap: 'break-word',
                    overflowWrap: 'break-word',
                },
            },
            {
                field: 'match_found',
                headerName: 'Match Found',
                flex: 1,
                minWidth: 120,
                cellClass: 'break-words',
                cellStyle: { wordWrap: 'break-word', overflowWrap: 'break-word' },
            },
            {
                field: 'score',
                headerName: 'Score',
                flex: 0.6,
                minWidth: 70,
                cellClass: 'text-right break-words',
                headerClass: 'text-right font-semibold text-default-700',
                cellStyle: { wordWrap: 'break-word', overflowWrap: 'break-word' },
            },
        ];

        return (
            <div className="overflow-x-auto w-full doc-invoice-line-grid-header-gray">
                <style>{`
                  .doc-invoice-line-grid-header-gray .ag-header,
                  .doc-invoice-line-grid-header-gray .ag-header-cell {
                    background-color: rgb(229 231 235) !important;
                  }
                  .doc-invoice-line-grid-header-gray .ag-layout-auto-height .ag-center-cols-viewport,
                  .doc-invoice-line-grid-header-gray .ag-layout-auto-height .ag-center-cols-container {
                    min-height: 0 !important;
                  }
                  .doc-invoice-line-grid-header-gray .ag-cell-wrapper {
                    align-items: flex-start;
                  }
                `}</style>
                <div className="ag-theme-alpine w-full" style={{ width: '100%', minWidth: '100%' }}>
                    <AgGridReact
                        rowData={rowData}
                        columnDefs={columnDefs}
                        defaultColDef={{
                            sortable: false,
                            resizable: true,
                            suppressMovable: true,
                            headerClass: 'text-left font-semibold text-default-700',
                        }}
                        domLayout="autoHeight"
                        pagination={false}
                        suppressPaginationPanel
                        headerHeight={40}
                        getRowId={(p) => String(p.data.id)}
                    />
                </div>
            </div>
        );
    };

    const renderDetailedSummary = () => {
        // Check if there's an error to display
        if (errorData) {
            return <ErrorDisplay errorData={errorData} />;
        }

        if (!explanationData || explanationData.length === 0) {
            return (
                <div className="text-center py-8 text-default-500">
                    <Icon icon="mdi:information-outline" className="w-8 h-8 mx-auto mb-2 text-default-400" />
                    <p>No explanation data available</p>
                </div>
            );
        }

        return (
            <div className="space-y-4">
                {/* Search Box */}
                <div className="mb-4 px-2">
                    <Input
                        type="text"
                        placeholder="Search by field name, value, or notes..."
                        value={searchQuery}
                        onChange={(e) => {
                            setSearchQuery(e.target.value);
                            setActiveStopGate(null);
                        }}
                        startContent={<Icon icon="mdi:magnify" className="text-default-400" style={{ width: '18px', height: '18px' }} />}
                        size="sm"
                        classNames={{
                            input: "text-sm",
                            inputWrapper: "border-default-300 h-8"
                        }}
                    />
                </div>

                {(subletFlags.sublet_found !== undefined || subletFlags.sublet_match_found !== undefined) && (
                    <div className="mb-3 px-2 flex flex-wrap gap-x-6 gap-y-1 text-sm text-default-700">
                        {subletFlags.sublet_found !== undefined && (
                            <span>
                                <span className="text-default-500">Sublet Found</span>
                                {': '}
                                <span className="font-medium">{formatValue(subletFlags.sublet_found)}</span>
                            </span>
                        )}
                        {subletFlags.sublet_match_found !== undefined && (
                            <span>
                                <span className="text-default-500">Sublet Match Found</span>
                                {': '}
                                <span className="font-medium">{formatValue(subletFlags.sublet_match_found)}</span>
                            </span>
                        )}
                    </div>
                )}

                <Accordion
                    variant="splitted"
                    selectionMode="multiple"
                    selectedKeys={selectedDetailKeys}
                    onSelectionChange={(keys) => setSelectedDetailKeys(keys as Set<string>)}
                    isCompact
                    className="gap-2 [&_hr]:hidden [&_.divider]:hidden"
                >
                    {filteredData.map((item: any, index: number) => {
                        // Extract data based on format
                        const criteria = isRepairOrderMatching 
                            ? formatValue(item.criteria)
                            : formatValue(item[criteriaKey]);
                        const rawScore = isRepairOrderMatching ? item.score : item[matchScoreKey];
                        const showScore = rawScore !== undefined && rawScore !== null && rawScore !== '';
                        const score = showScore ? rawScore : 0;
                        const numScore = parseScore(score);
                        const scoreColor = getScoreColor(numScore);
                        const scoreDisplay = `${Math.round(numScore)}%`;

                        // Check if this is a Service Event (case-insensitive)
                        const normalizedCriteria = criteria?.toLowerCase().replace(/_/g, ' ') || '';
                        const isServiceEvent = normalizedCriteria === 'service event';

                        // Generate unique key
                        const uniqueKey = isRepairOrderMatching
                            ? `detail-${criteria}-${score}-${index}`
                            : (() => {
                                const originalIndex = explanationData.findIndex((origItem: any) =>
                                    formatValue(origItem[criteriaKey]) === criteria
                                );
                                return `detail-${originalIndex >= 0 ? originalIndex : index}`;
                            })();

                        return (
                            <AccordionItem
                                key={uniqueKey}
                                aria-label={criteria}
                                title={
                                    <div className="flex justify-between items-center w-full pr-4">
                                        <span className="font-medium text-default-700">{criteria}</span>
                                        {showScore && renderScoreChip(scoreColor, scoreDisplay)}
                                    </div>
                                }
                                classNames={{
                                    content: "pl-1 pr-0",
                                    base: "w-full",
                                    title: "text-base"
                                }}
                            >
                                <div className="pb-2 space-y-2">
                                    {renderAccordionContent(item, isServiceEvent)}
                                </div>
                            </AccordionItem>
                        );
                    })}
                </Accordion>
            </div>
        );
    };

    // Helper function to calculate display score
    const calculateDisplayScore = (): string | null => {
        if (totalScore) {
                    const parsedScore = Number.parseFloat(totalScore.replace('%', '').trim());
            if (Number.isFinite(parsedScore)) {
                return `${Math.round(parsedScore)}%`;
            }
            return totalScore;
        }
        
        if (agentScore !== '' && agentScore !== null && agentScore !== undefined) {
            return `${Math.round(parseScore(agentScore))}%`;
        }
        
        return null;
    };

    // Helper function to calculate score value
    const calculateScoreValue = (): number => {
        if (totalScore) {
            return Number.parseFloat(totalScore.replace('%', '').trim());
        }
        return parseScore(agentScore);
    };

    const renderMainContent = () => {
        const displayScore = calculateDisplayScore();
        const scoreValue = calculateScoreValue();
        const scoreColor = getScoreColor(scoreValue);

        const overviewContent = (
            <Accordion
                variant="splitted"
                selectionMode="multiple"
                selectedKeys={selectedKeys}
                onSelectionChange={(keys) => setSelectedKeys(keys as Set<string>)}
                isCompact
                className="gap-2 [&_hr]:hidden [&_.divider]:hidden"
            >
                <AccordionItem
                    key="ai-summary"
                    aria-label="AI Summary"
                    title={
                        <div className="flex items-center gap-3">
                            <Icon icon="mdi:sparkles" className="text-pink-600" style={{ width: '18px', height: '18px', flexShrink: 0 }} />
                            <div>
                                <div className="renderer-text-primary">AI Summary</div>
                                <div className="renderer-text-secondary">AI-generated analysis overview</div>
                            </div>
                        </div>
                    }
                    className="mb-3"
                >
                    <div className="pt-1 pb-2">
                        <div className="bg-content2 rounded-lg p-2">
                            <div className="text-sm text-default-700 prose prose-sm max-w-none markdown-body overflow-x-auto"
                                style={{
                                    color: 'black',
                                    backgroundColor: 'transparent',
                                    display: 'inline-block',
                                    maxWidth: '100%'
                                }}>
                                <ReactMarkdown remarkPlugins={[remarkGfm]} components={{
                                    ul: ({ node, ...props }) => <ul className="list-disc" {...props} />,
                                    ol: ({ node, ...props }) => <ol className="list-decimal" {...props} />,
                                    table: ({ node, ...props }) => (
                                        <table className="w-full border-collapse border border-default-300 table-fixed" style={{ tableLayout: 'fixed', width: '100%' }} {...props} />
                                    ),
                                    td: ({ node, children, ...props }) => (
                                        <td className="border border-default-300 px-3 py-2 text-left break-words" style={{ wordWrap: 'break-word', overflowWrap: 'break-word' }} {...props} title={typeof children === 'string' ? children : String(children)}>
                                            {children}
                                        </td>
                                    ),
                                    th: ({ node, children, ...props }) => (
                                        <th className="border border-default-300 px-3 py-2 text-left font-semibold bg-content2 break-words" style={{ wordWrap: 'break-word', overflowWrap: 'break-word' }} scope="col" {...props} title={typeof children === 'string' ? children : String(children)}>
                                            {children}
                                        </th>
                                    ),
                                }}>
                                    {aiSummary?.replace(/\n/g, '  \n')}
                                </ReactMarkdown>
                            </div>
                        </div>
                    </div>
                </AccordionItem>

                        <AccordionItem
                            key="detailed-summary"
                            aria-label="Detailed Summary"
                            title={
                                <div className="flex items-center justify-between w-full pr-4">
                                    <div className="flex items-center gap-3">
                                        <Icon icon="mdi:file-document-check" className="text-green-600" style={{ width: '18px', height: '18px', flexShrink: 0 }} />
                                        <div>
                                            <div className="renderer-text-primary" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                                Detailed Summary
                                                {explanationData && explanationData.length > 0 && (
                                                    <Chip size="sm" variant="flat" className="bg-default-100" style={{ fontSize: '14px' }}>
                                                        {explanationData.length}/{explanationData.length}
                                                    </Chip>
                                                )}
                                            </div>
                                            <div className="renderer-text-secondary">Field-by-field comparison</div>
                                        </div>
                                    </div>
                                </div>
                            }
                            classNames={{
                                content: "pl-1 pr-0",
                                base: "w-full",
                                title: "text-base"
                            }}
                            className="mb-3"
                        >
                            <div className="pt-1 pb-2">
                                {renderDetailedSummary()}
                            </div>
                        </AccordionItem>
                    </Accordion>
        );

        return (
            <div className={`flex flex-col h-full invoice-matcher-renderer ${className}`}>
                <div className="border-b-3 border-default-200 bg-default-100 m-2 rounded-md">
                {/* Header Section */}
                <RendererHeader
                    agentName={agentName}
                    duration={duration}
                    entityNumber={entityNumber}
                    timestamp={timestamp}
                    isModal={isModal}
                    onFullScreenClick={() => onRequestFullScreen ? onRequestFullScreen() : setIsFullScreenOpen(true)}
                    onRefresh={onRefresh}
                />

                {/* Score Card */}
                <div className="flex-none pl-3 pr-3">
                    <ScoreCard
                        displayScore={displayScore}
                        scoreValue={scoreValue}
                        scoreColor={scoreColor}
                        matchBreakdown={matchBreakdown}
                        activeFilter={activeFilter}
                        onFilterClick={handleFilterClick}
                        feedbackComponent={feedbackComponent}
                        stopGates={stopGateItems}
                        activeStopGate={activeStopGate}
                        onStopGateClick={handleStopGateClick}
                    />
                </div>
                </div>

                {/* Tabs Section */}
                <RendererTabs
                    activeTab={activeTab}
                    onTabChange={(key) => setActiveTab(key)}
                    isExpanded={isExpanded}
                    onExpandAll={handleExpandAll}
                    knowledgeComponent={knowledgeComponent}
                    overviewContent={overviewContent}
                />

                {showActionButtons && data?.action_button && data.action_button.length > 0 && (
                    <div className="flex-none p-3 border-t border-default-200 bg-content2">
                        <div className="flex gap-3 justify-center">
                            {data.action_button.map((button: any, index: number) => (
                                <button
                                    key={index}
                                    onClick={() => onButtonAction?.(button.action || 'click', button)}
                                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                                >
                                    {button.label || button.name || `Action ${index + 1}`}
                                </button>
                            ))}
                        </div>
                    </div>
                )}
            </div>
        );
    };

    return (
        <>
            {renderMainContent()}

            <FullScreenModal
                isOpen={isFullScreenOpen}
                onClose={() => setIsFullScreenOpen(false)}
                agentName={agentName}
                modalClassName="invoice-matcher-renderer"
            >
                <InvoiceMatcherRenderer
                    claimData={claimData}
                    onButtonAction={onButtonAction}
                    showActionButtons={showActionButtons}
                    knowledgeComponent={knowledgeComponent}
                    feedbackComponent={feedbackComponent}
                    isModal={true}
                    source={source}
                />
            </FullScreenModal>
        </>
    );
};

export { InvoiceMatcherRenderer };
export default InvoiceMatcherRenderer; 